import { useCallback, useRef, FC, useContext, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { Button } from "@src/lib/ui/Button";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/18px/x";
import FileIcon from "@src/lib/icons/18px/file-outline";
import { FileUploadContext } from "@src/lib/contexts/FileUploadContext";
import { PresignedS3PostDataBucket } from "@src/types.generated";
import { cn } from "@src/lib/utils/cn";

type Props = {
  loading: boolean;
  isValidating?: boolean;
  onUploadComplete?: (key: string) => void;
};

const AIUploadArticulateDialog: FC<Props> = ({
  loading,
  isValidating,
  onUploadComplete,
}) => {
  const {
    setUploadedFile,
    uploadedFileKey,
    setUploadedFileKey,
    uploadedFileName,
    setUploadedFileName,
  } = useContext(FileUploadContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadProgress: (fileId, progress) => {
      setUploadProgress(progress);
    },
    onUploadComplete: (upload) => {
      setUploadedFileKey(upload.key);
      setUploadedFileName(upload.name);
      onUploadComplete?.(upload.key);
    },
    bucket: PresignedS3PostDataBucket.Scorm,
  });

  const handleFileSelection = useCallback(
    (selectedFile: File) => {
      setUploadedFile(selectedFile);
      handleFileUpload(selectedFile, "articulate-upload");
    },
    [handleFileUpload, setUploadedFile],
  );

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFileSelection(acceptedFiles[0]);
      }
    },
    [handleFileSelection],
  );

  const { getRootProps, isDragActive } = useDropzone({
    useFsAccessApi: true,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: { "application/zip": [".zip"] },
    onDrop,
  });

  const clearUpload = useCallback(() => {
    setUploadedFileKey(null);
    setUploadedFileName(null);
    setUploadedFile(null);
  }, [setUploadedFileKey, setUploadedFileName, setUploadedFile]);

  const isDisabled = loading || isUploading || isValidating;

  return (
    <div
      className={cn(
        "flex h-[320px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed bg-background",
        isDragActive && !uploadedFileKey
          ? "bg-gray-2 ring-2 ring-primary ring-offset-2"
          : "border-gray-5/50",
      )}
      {...(uploadedFileKey ? {} : getRootProps())}
    >
      <div className="flex h-[87px] items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <Text variant="body-title" className="text-center">
            Upload Articulate 360 Rise Package
          </Text>
          <Text
            variant="compact-description"
            className="mt-4 text-center text-gray-8"
          >
            AI Assist will convert your Articulate Rise 360 package into a
            module
          </Text>

          {uploadedFileKey && uploadedFileName && (
            <div className="mt-4 flex items-center gap-2">
              <Button variant="ghost" size="xs" onClick={clearUpload}>
                <XIcon />
              </Button>
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-5 px-3 py-2">
                <FileIcon className="size-4 text-gray-8" />
                <Text variant="compact-description" className="text-gray-8">
                  {uploadedFileName}
                </Text>
              </div>
            </div>
          )}

          {(isUploading || isValidating) && (
            <div className="mt-4 flex flex-col items-center justify-center gap-3">
              <Spinner />
              <Text variant="compact-description">
                {isValidating
                  ? "Validating..."
                  : `Uploading ${uploadProgress}%`}
              </Text>
            </div>
          )}
          {!isDisabled && !uploadedFileKey && (
            <>
              <div className="mt-3 flex items-center">
                <Button
                  variant="default"
                  onClick={openFilePicker}
                  disabled={isDisabled}
                >
                  Upload ZIP file
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".zip"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelection(file);
                  }}
                />
              </div>
              <Text
                variant="compact-description"
                className="mt-4 text-center text-gray-5"
              >
                Or drag & drop file here
              </Text>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIUploadArticulateDialog;
